import React, { useState, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import EncodingSelector from './EncodingSelector';

const DropzoneContainer = styled(Paper)(({ theme, isDragActive }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
  backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  }
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const FileUpload = ({ onFileSelect, acceptedFiles = ['.txt'], maxSize = 5 * 1024 * 1024 }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [showEncodingSelector, setShowEncodingSelector] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);

  const validateFile = useCallback((file) => {
    setError(null);

    // Sprawdź rozszerzenie pliku
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedFiles.includes(fileExtension)) {
      setError(`Nieprawidłowy format pliku. Dozwolone formaty: ${acceptedFiles.join(', ')}`);
      return false;
    }

    // Sprawdź rozmiar pliku
    if (file.size > maxSize) {
      setError(`Plik jest za duży. Maksymalny rozmiar: ${(maxSize / 1024 / 1024).toFixed(1)}MB`);
      return false;
    }

    return true;
  }, [acceptedFiles, maxSize]);

  const handleFile = useCallback((file) => {
    if (!validateFile(file)) {
      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);

    // Symulacja progresja odczytu pliku
    const reader = new FileReader();

    reader.onloadstart = () => {
      setUploadProgress(10);
    };

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 80) + 10;
        setUploadProgress(progress);
      }
    };

    reader.onload = (event) => {
      setUploadProgress(100);
      let content = event.target.result;

      // Jeśli zawiera znaki zapytania, może to oznaczać problem z kodowaniem
      if (content.includes('�') || content.includes('?')) {
        // Pokaż selektor kodowania
        setCurrentFile(file);
        setShowEncodingSelector(true);
        setUploadProgress(0);
        return;
      }

      processContent(content);
    };

    const processContent = (content) => {
      // Sprawdź czy plik zawiera dane MT940
      if (!content.includes(':20:') || !content.includes(':61:')) {
        setError('Plik nie zawiera prawidłowych danych MT940');
        setSelectedFile(null);
        setUploadProgress(0);
        return;
      }

      // Przekaż dane do komponentu nadrzędnego
      onFileSelect({
        file,
        content,
        name: file.name,
        size: file.size
      });
    };

    reader.onerror = () => {
      setError('Błąd podczas odczytu pliku');
      setSelectedFile(null);
      setUploadProgress(0);
    };

    reader.readAsText(file, 'utf-8');
  }, [validateFile, onFileSelect]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setUploadProgress(0);
    setError(null);
    setShowEncodingSelector(false);
    setCurrentFile(null);
    onFileSelect(null);
  }, [onFileSelect]);

  const handleEncodingSelected = useCallback((data) => {
    setShowEncodingSelector(false);
    setCurrentFile(null);
    setSelectedFile(data.file);
    onFileSelect(data);
  }, [onFileSelect]);

  const handleEncodingCancelled = useCallback(() => {
    setShowEncodingSelector(false);
    setCurrentFile(null);
    setSelectedFile(null);
    setUploadProgress(0);
    setError('Anulowano wybór kodowania pliku');
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {!selectedFile ? (
        <DropzoneContainer
          elevation={1}
          isDragActive={isDragActive}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => document.getElementById('file-input').click()}
        >
          <CloudUpload
            sx={{
              fontSize: 64,
              color: isDragActive ? 'primary.main' : 'grey.400',
              mb: 2
            }}
          />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Upuść plik tutaj' : 'Przeciągnij i upuść plik MT940'}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            lub kliknij, aby wybrać plik
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Dozwolone formaty: {acceptedFiles.join(', ')} • Max {(maxSize / 1024 / 1024).toFixed(1)}MB
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            💡 Jeśli widzisz znaki zapytania zamiast polskich liter, aplikacja automatycznie pomoże wybrać właściwe kodowanie
          </Typography>

          <HiddenInput
            id="file-input"
            type="file"
            accept={acceptedFiles.join(',')}
            onChange={handleInputChange}
          />
        </DropzoneContainer>
      ) : (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <InsertDriveFile color="primary" />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              <Chip
                label="MT940"
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
            <IconButton onClick={handleRemoveFile} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Odczytywanie pliku... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <EncodingSelector
        open={showEncodingSelector}
        file={currentFile}
        onEncodingSelected={handleEncodingSelected}
        onCancel={handleEncodingCancelled}
      />
    </Box>
  );
};

export default FileUpload;